import { ApiService } from "./apiService";
import { buildQuery, withIsActive, failure } from "./serviceHelpers";
import { fetchAllPages, unwrapPaginated } from "../utils/paginationHelper";
import {
  BiaFitUser,
  CreateUserInput,
  UpdateUserInput,
  UserFilters,
  UsersResponse,
  UserOperationResult,
  Program,
  BiaFitUserWithPrograms,
  BiaFitUserDetailed,
  DetailedUserApiResponse,
  UsersApiResponse,
} from "../types/user";
import { logger } from "../utils/logger";

// The user list and its CSV export must send identical filters - they were
// two copy-pasted ladders before, with a comment admitting as much.
function userFilterQuery(filters: UserFilters, page?: number): string {
  return buildQuery({
    page,
    with_trashed: filters.with_trashed,
    searchTerm: filters.search,
    subscription_provider: filters.subscription_provider,
    subscription_status: filters.subscription_status,
    is_challenge_participant: filters.is_challenge_participant,
    challenge_id: filters.challenge_id,
  });
}

export class UserService {
  // CREATE - Add a new user
  static async createUser(
    userData: CreateUserInput
  ): Promise<UserOperationResult> {
    try {
      const response = await ApiService.post("/users", userData);
      const result = await ApiService.handleResponse<{ data: BiaFitUser }>(
        response
      );

      if (result.data) {
        return {
          success: true,
          data: withIsActive(result.data),
        };
      }

      logger.error("Unexpected create user API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // READ - Get all users with pagination and filters
  static async getUsers(
    page: number = 1,
    filters: UserFilters = {}
  ): Promise<UsersResponse | null> {
    try {
      const response = await ApiService.get(
        `/users/filter${userFilterQuery(filters, page)}`
      );
      const result = await ApiService.handleResponse<UsersApiResponse>(
        response
      );

      const paged = unwrapPaginated(result, "users");
      if (!paged) return null;

      return {
        users: paged.items.map(withIsActive),
        total: paged.total,
        page: paged.page,
        limit: paged.limit,
      };
    } catch (err: any) {
      logger.error("Error fetching users:", err);
      return null;
    }
  }

  // READ - Get a single user by ID
  static async getUserById(userId: number): Promise<BiaFitUser | null> {
    try {
      const response = await ApiService.get(`/users/${userId}`);
      const result = await ApiService.handleResponse<{ data: BiaFitUser }>(
        response
      );

      // Handle the response structure where user data is nested in 'data'
      if (result.data) {
        return withIsActive(result.data);
      }

      logger.error("Unexpected user API response format:", result);
      return null;
    } catch (err: any) {
      logger.error("Error fetching user:", err);
      return null;
    }
  }

  // READ - Get detailed user by ID (includes fitness_data and weight_progress_images)
  static async getUserDetailed(
    userId: number
  ): Promise<BiaFitUserDetailed | null> {
    try {
      const response = await ApiService.get(`/users/show-detailed/${userId}`);
      const result = await ApiService.handleResponse<DetailedUserApiResponse>(
        response
      );

      if (result.data) {
        return withIsActive(result.data);
      }

      logger.error("Unexpected detailed user API response format:", result);
      return null;
    } catch (err: any) {
      logger.error("Error fetching detailed user:", err);
      return null;
    }
  }

  // UPDATE - Update user information
  static async updateUser(
    userId: number,
    updates: UpdateUserInput
  ): Promise<UserOperationResult> {
    try {
      const response = await ApiService.patch(`/users/${userId}`, updates);
      const result = await ApiService.handleResponse<{ data: BiaFitUser }>(
        response
      );

      if (result.data) {
        return {
          success: true,
          data: withIsActive(result.data),
        };
      }

      logger.error("Unexpected update user API response format:", result);
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // DELETE - Hard delete user
  static async deleteUser(userId: number): Promise<UserOperationResult> {
    try {
      const response = await ApiService.delete(`/users/${userId}`);
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // PROGRAMS - Get user with their programs
  static async getUserWithPrograms(
    userId: number
  ): Promise<BiaFitUserWithPrograms | null> {
    try {
      // Get user with their programs in one request
      const response = await ApiService.get(`/users/${userId}/programs`);
      const result = await ApiService.handleResponse<any>(response);

      // Handle different possible response formats
      if (result.user && result.programs) {
        return {
          ...withIsActive(result.user),
          programs: result.programs,
        };
      } else if (result.programs !== undefined) {
        // If it's just a user object with programs property
        return {
          ...withIsActive(result),
          programs: result.programs || [],
        };
      } else {
        // Fallback: get user separately and assume no programs
        const user = await this.getUserById(userId);
        if (user) {
          return {
            ...user,
            programs: [],
          };
        }
        return null;
      }
    } catch (err: any) {
      logger.error("Error fetching user with programs:", err);
      // Fallback to getting just the user without programs
      const user = await this.getUserById(userId);
      if (user) {
        return {
          ...user,
          programs: [],
        };
      }
      return null;
    }
  }

  // PROGRAMS - Get all available programs (using new training-programs API)
  static async getAllPrograms(): Promise<Program[]> {
    try {
      const allPrograms = await fetchAllPages<any>("/admin/training-programs");

      // Convert TrainingProgram to Program format for compatibility
      return allPrograms.map((program: any) => ({
        id: program.id,
        title: program.name, // Map name to title for backward compatibility
        description: program.description,
        created_at: program.created_at,
      }));
    } catch (err: any) {
      logger.error("Error fetching programs:", err);
      return [];
    }
  }

  // PROGRAMS - Search programs with smart pagination
  static async searchPrograms(
    searchTerm: string = "",
    page: number = 1
  ): Promise<{
    programs: Program[];
    hasMore: boolean;
    totalPages: number;
    currentPage: number;
    total: number;
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
      });

      if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
      }

      const endpoint = searchTerm.trim()
        ? `/training-programs/search?${params.toString()}`
        : `/training-programs?${params.toString()}`;

      const response = await ApiService.get(endpoint);
      const result = await ApiService.handleResponse<any>(response);

      const paged = unwrapPaginated<any>(result, "programs search");
      if (paged) {
        const programs = paged.items.map((program: any) => ({
          id: program.id,
          title: program.name, // Map name to title for backward compatibility
          description: program.description,
          created_at: program.created_at,
        }));

        // Only show "Load More" if we have more than 60 results total
        const hasMore = paged.total > 60 && paged.page < paged.lastPage;

        return {
          programs,
          hasMore,
          totalPages: paged.lastPage || 1,
          currentPage: paged.page || 1,
          total: paged.total || 0,
        };
      } else {
        logger.error("Unexpected programs search API response format:", result);
        return {
          programs: [],
          hasMore: false,
          totalPages: 0,
          currentPage: 1,
          total: 0,
        };
      }
    } catch (err: any) {
      logger.error("Error searching programs:", err);
      return {
        programs: [],
        hasMore: false,
        totalPages: 0,
        currentPage: 1,
        total: 0,
      };
    }
  }

  // PROGRAMS - Get available programs for a user (programs they're not assigned to)
  static async getAvailableProgramsForUser(userId: number): Promise<Program[]> {
    try {
      // Get all programs
      const allPrograms = await this.getAllPrograms();

      // Get user's current programs
      const userWithPrograms = await this.getUserWithPrograms(userId);
      const userProgramIds = userWithPrograms?.programs?.map((p) => p.id) || [];

      // Filter out programs user is already assigned to
      return allPrograms.filter(
        (program) => !userProgramIds.includes(program.id)
      );
    } catch (err: any) {
      logger.error("Error fetching available programs for user:", err);
      return [];
    }
  }

  // Delete user profile image
  static async deleteProfileImage(
    userId: number,
    fileId: number
  ): Promise<UserOperationResult> {
    try {
      const response = await ApiService.delete(`/files/delete/${fileId}`);
      await ApiService.handleResponse(response);

      return {
        success: true,
      };
    } catch (err) {
      return failure(err, "Failed to delete profile image");
    }
  }

  // UPDATE - Update user fitness data
  static async updateUserFitnessData(
    userId: number,
    fitnessData: any
  ): Promise<UserOperationResult> {
    try {
      const response = await ApiService.patch(`/users/${userId}`, {
        fitness_data: fitnessData,
      });
      const result = await ApiService.handleResponse<{
        data: BiaFitUserDetailed;
      }>(response);

      if (result.data) {
        return {
          success: true,
          data: withIsActive(result.data),
        };
      }

      logger.error(
        "Unexpected update fitness data API response format:",
        result
      );
      return { success: false, error: "Unexpected response format" };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // Upload user profile image
  static async uploadProfileImage(
    imageFile: File
  ): Promise<UserOperationResult> {
    try {
      const formData = new FormData();
      formData.append("file", imageFile);

      const response = await ApiService.post(
        "/user-files/upload-profile-image",
        formData
      );

      const result = await ApiService.handleResponse<{ data: BiaFitUser }>(
        response
      );

      return {
        success: true,
        data: result.data,
      };
    } catch (err) {
      return failure(err, "Failed to upload profile image");
    }
  }

  // DEPRECATED: Use getUsers with filters instead
  // Kept for backward compatibility but now uses the new /users/filter endpoint
  static async searchUsers(
    searchTerm: string,
    page: number = 1,
    limit: number = 50
  ): Promise<UsersApiResponse | null> {
    try {
      const params = new URLSearchParams({
        searchTerm: searchTerm.trim(),
        page: page.toString(),
        with_trashed: "true",
      });

      // Use the new /users/filter endpoint for consistency
      const response = await ApiService.get(
        `/users/filter?${params.toString()}`
      );
      const result = await ApiService.handleResponse<UsersApiResponse>(
        response
      );

      if (!unwrapPaginated(result, "users search")) return null;

      // Spread the paginator through rather than rebuilding five of its
      // fields - the hand-picked subset silently dropped links/next_page_url.
      return {
        data: {
          ...result.data,
          data: result.data.data.map(withIsActive),
        },
      };
    } catch (err: any) {
      logger.error("Error searching users:", err);
      return null;
    }
  }

  // Change user password
  static async changeUserPassword(
    userId: number,
    password: string,
    passwordConfirmation: string
  ): Promise<UserOperationResult> {
    try {
      const response = await ApiService.post(
        `/users/${userId}/update-password`,
        {
          password: password,
          password_confirmation: passwordConfirmation,
        }
      );
      await ApiService.handleResponse(response);

      return { success: true };
    } catch (err) {
      return failure(err, "An unexpected error occurred");
    }
  }

  // Export users as CSV. Shares userFilterQuery with getUsers so the export
  // can never drift from the list it claims to mirror, and goes through
  // ApiService so an expired token is refreshed rather than failing outright.
  static async exportUsersCsv(filters: UserFilters = {}): Promise<void> {
    const blob = await ApiService.getBlob(
      `/users/filter/export-csv${userFilterQuery(filters)}`
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users-export.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}
